import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../api/client';
import { useAuth } from './useAuth';

const BranchContext = createContext(null);

const STORAGE_KEY = 'selected_branch_id';

/**
 * Контекст текущего филиала для шапки и X-Branch-Id.
 * manager/expeditor — фиксированный из user.branch_id;
 * admin — выбор из списка, в localStorage.
 */
export function BranchProvider({ children }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchIdState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? Number(saved) : null;
  });
  const [loaded, setLoaded] = useState(false);

  const fetchBranches = useCallback(async () => {
    if (!user) {
      setBranches([]);
      setLoaded(false);
      return;
    }
    try {
      const resp = await api.get('/branches', { params: { active_only: true } });
      const list = Array.isArray(resp.data) ? resp.data : [];
      setBranches(list);
      if (user.role === 'admin' && list.length > 0) {
        setSelectedBranchIdState((prev) => {
          const ok = list.some((b) => b.id === prev);
          if (ok) return prev;
          const next = list[0].id;
          localStorage.setItem(STORAGE_KEY, String(next));
          return next;
        });
      }
    } catch {
      setBranches([]);
    } finally {
      setLoaded(true);
    }
  }, [user]);

  useEffect(() => { fetchBranches(); }, [fetchBranches]);

  const setSelectedBranchId = useCallback((id) => {
    const num = id == null ? null : Number(id);
    setSelectedBranchIdState(num);
    if (num != null) localStorage.setItem(STORAGE_KEY, String(num));
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const effectiveBranchId = useMemo(() => {
    if (!user) return null;
    if (user.role === 'admin') return selectedBranchId;
    return user.branch_id ?? null;
  }, [user, selectedBranchId]);

  const currentBranch = useMemo(() => {
    if (!user) return null;
    if (user.role !== 'admin') {
      if (!user.branch_id) return null;
      return branches.find((b) => b.id === user.branch_id)
        || (user.branch_name ? { id: user.branch_id, name: user.branch_name } : null);
    }
    return branches.find((b) => b.id === selectedBranchId) || null;
  }, [user, branches, selectedBranchId]);

  const value = useMemo(() => ({
    branches,
    loaded,
    selectedBranchId,
    setSelectedBranchId,
    effectiveBranchId,
    currentBranch,
    refreshBranches: fetchBranches,
    canSwitch: user?.role === 'admin' && branches.length > 1,
  }), [
    branches, loaded, selectedBranchId, setSelectedBranchId,
    effectiveBranchId, currentBranch, fetchBranches, user,
  ]);

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  const ctx = useContext(BranchContext);
  if (!ctx) throw new Error('useBranch must be used within BranchProvider');
  return ctx;
}
